import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

const READY = ["Pronta per preventivo", "Preventivo pronto", "Preventivo accettato", "Ordine confermato", "Lavoro completato"];
const WARN = ["Dati mancanti", "Attesa cliente", "Cliente da richiamare", "Da controllare", "Sopralluogo da fissare"];
const INFO = ["Preventivo inviato", "Sopralluogo fissato", "Preventivo in preparazione", "In produzione / ordinato", "Montaggio pianificato"];

const STATUS_KEY = {
  "Nuova richiesta": "nuova_richiesta", "Dati mancanti": "dati_mancanti", "Attesa cliente": "attesa_cliente",
  "Cliente da richiamare": "cliente_da_richiamare", "Da controllare": "da_controllare",
  "Pronta per preventivo": "pronta_preventivo", "Preventivo in preparazione": "preventivo_in_preparazione",
  "Preventivo pronto": "preventivo_pronto", "Preventivo inviato": "preventivo_inviato",
  "Preventivo accettato": "preventivo_accettato", "Preventivo rifiutato": "preventivo_rifiutato",
  "Sopralluogo fissato": "sopralluogo_fissato", "Sopralluogo da fissare": "sopralluogo_da_fissare", "Ordine confermato": "ordine_confermato",
  "In produzione / ordinato": "in_produzione", "Montaggio pianificato": "montaggio_pianificato",
  "Lavoro completato": "lavoro_completato", "Archiviata": "archiviata",
};
const PRIORITY_KEY = { Alta: "alta", Media: "media", Bassa: "bassa" };

export function tStatus(t, status) {
  const k = STATUS_KEY[status];
  return k ? t(`status.${k}`) : status;
}
export function tPriority(t, p) {
  const k = PRIORITY_KEY[p];
  return k ? t(`priority.${k}`) : p;
}

// Catalog display label: master items translate by canonical key; custom items keep tenant label.
export function catLabel(t, item) {
  if (!item) return "";
  return item.is_custom ? item.label : t(`catalogm.${item.type}_${item.key}`, item.label);
}

export function StatusBadge({ status }) {
  const { t } = useTranslation();
  let cls = "bg-slate-100 text-slate-700 border-slate-200";
  if (READY.includes(status)) cls = "bg-emerald-50 text-emerald-700 border-emerald-200";
  else if (WARN.includes(status)) cls = "bg-amber-50 text-amber-800 border-amber-200";
  else if (INFO.includes(status)) cls = "bg-sky-50 text-sky-700 border-sky-200";
  else if (status === "Preventivo rifiutato" || status === "Archiviata") cls = "bg-slate-100 text-slate-500 border-slate-200";
  else if (status === "Nuova richiesta") cls = "bg-indigo-50 text-indigo-700 border-indigo-200";
  return <Badge data-testid="status-badge" variant="outline" className={`${cls} font-medium`}>{tStatus(t, status)}</Badge>;
}

export function PriorityDot({ priority }) {
  const c = priority === "Alta" ? "bg-red-500" : priority === "Bassa" ? "bg-emerald-500" : "bg-amber-500";
  return <span className={`inline-block w-2.5 h-2.5 rounded-full ${c}`} />;
}

export function PriorityBadge({ priority }) {
  const { t } = useTranslation();
  const map = {
    Alta: "bg-red-50 text-red-700 border-red-200",
    Media: "bg-amber-50 text-amber-800 border-amber-200",
    Bassa: "bg-emerald-50 text-emerald-700 border-emerald-200",
  };
  return <Badge variant="outline" className={map[priority] || map.Media}><PriorityDot priority={priority} /><span className="ml-1.5">{tPriority(t, priority)}</span></Badge>;
}

export function Loading({ label }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-center py-20 text-slate-400 gap-2">
      <Loader2 className="w-5 h-5 animate-spin" /> {label || t("common.loading")}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, hint, children }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      {Icon && <Icon className="w-10 h-10 text-slate-300 mb-3" />}
      <p className="text-slate-700 font-semibold">{title}</p>
      {hint && <p className="text-sm text-slate-400 mt-1 max-w-md">{hint}</p>}
      {children && <div className="mt-4">{children}</div>}
    </div>
  );
}
